import { EarningsPeriod } from '@api/api/earnings';
import { Entity } from '@api/api/entity-schema';
import { Button } from '@design/components/button/Button';
import { ButtonLink } from '@design/components/button-link/ButtonLink';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { RevampTable, TableCell, TableHeader } from '@design/components/table';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import {
  SubTabsType,
  useEarningsInsightDrawer,
} from '@terminal/widgets/earnings-insight-drawer/useEarningsInsightDrawer';
import clsx from 'clsx';
import { Fragment, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './TopCompetitors.module.scss';
import { useTopCompetitorColumns } from './useTopCompetitorColumns';
import { useTopCompetitorsData } from './useTopCompetitorsData';
import { COLUMN_COUNT, TOP_COMPETITORS_LIMIT } from './utils';

export interface TopCompetitorsProps {
  entity: Entity;
  activeReportingPeriod: EarningsPeriod;
}

export const TopCompetitors = ({
  entity,
  activeReportingPeriod,
}: TopCompetitorsProps) => {
  const { t } = useTranslation(['earnings']);
  const [expandedTag, setExpandedTag] = useState<string | null>();
  const columns = useTopCompetitorColumns();
  const { openDrawer } = useEarningsInsightDrawer();
  const {
    trackPeerComparisonRowClicked,
    trackPeerComparisonReadEarningsInsightClicked,
  } = usePosthogEvents();

  const { rows, isLoading, error } = useTopCompetitorsData({
    entityTag: entity.tag,
    activeReportingPeriod,
  });

  if (error) {
    throw new Error('Failed to load TopCompetitors');
  }

  const activeExpandedTag =
    expandedTag === undefined ? rows[0]?.entity.tag : expandedTag;

  const toggleRow = (tag: string) =>
    setExpandedTag(activeExpandedTag === tag ? null : tag);

  const renderLoadingRows = () =>
    Array.from({ length: TOP_COMPETITORS_LIMIT }).map((_, index) => (
      <tr key={index}>
        <TableCell
          className={styles.cell}
          colspan={COLUMN_COUNT}
          isNumber={false}
        >
          <SkeletonLoader
            areas={[
              {
                styles: { width: '100%', height: '25px', borderRadius: '8px' },
              },
            ]}
          />
        </TableCell>
      </tr>
    ));

  return (
    <section className={styles.root} data-testid="top-competitors">
      <h3 className={styles.title}>
        {t('earnings:peerComparison.topCompetitors.title', {
          name: entity.name_short || entity.name,
          count: TOP_COMPETITORS_LIMIT,
        })}
      </h3>
      <div className={styles.tableWrapper}>
        <RevampTable withBodyRowHover={false} withBodyRowCursor={false}>
          <thead>
            <tr>
              <TableHeader
                className={clsx(styles.header, styles.expandColumn)}
                isHoverEnabled={false}
              />
              {columns.map(column => (
                <TableHeader
                  key={column.key}
                  className={clsx(styles.header, column.className)}
                  alignment={column.alignment}
                  isHoverEnabled={false}
                >
                  {column.label}
                </TableHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              renderLoadingRows()
            ) : rows.length === 0 ? (
              <tr>
                <TableCell
                  className={clsx(styles.cell, styles.placeholder)}
                  colspan={COLUMN_COUNT}
                  alignment="start"
                  isNumber={false}
                >
                  {t('earnings:noDataToDisplay')}
                </TableCell>
              </tr>
            ) : (
              rows.map(row => {
                const {
                  entity: competitor,
                  earnings,
                  insightSummary,
                  earningsId,
                  hasEarningsInsight,
                } = row;
                const { tag } = competitor;
                const name = competitor.name_short || competitor.name;
                const isExpanded = activeExpandedTag === tag;

                const onRowClick = () => {
                  trackPeerComparisonRowClicked(
                    POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
                    tag,
                    earnings.reportingPeriod
                  );
                  toggleRow(tag);
                };

                const onChevronClick = (
                  event: MouseEvent<HTMLButtonElement>
                ) => {
                  event.stopPropagation();
                  onRowClick();
                };

                const onReadInsightClick = (
                  event: MouseEvent<HTMLButtonElement>
                ) => {
                  event.stopPropagation();
                  trackPeerComparisonReadEarningsInsightClicked(
                    POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
                    tag,
                    earnings.reportingPeriod
                  );

                  if (!earningsId) {
                    return;
                  }

                  openDrawer({
                    entityTag: tag,
                    earningsId,
                    subtab: earnings.hasReported
                      ? SubTabsType.Recap
                      : SubTabsType.Preview,
                    source: POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
                  });
                };

                return (
                  <Fragment key={tag}>
                    <tr
                      className={clsx(styles.competitorRow, {
                        [styles.expanded]: isExpanded,
                      })}
                      onClick={onRowClick}
                      data-testid="top-competitors-row"
                    >
                      <TableCell
                        className={clsx(styles.cell, styles.expandColumn)}
                        alignment="center"
                        isNumber={false}
                      >
                        <Button
                          variant="empty"
                          size="xsmall"
                          iconName={
                            isExpanded ? 'ChevronLightUp' : 'ChevronLightDown'
                          }
                          aria-label={t(
                            isExpanded
                              ? 'earnings:peerComparison.topCompetitors.collapseRow'
                              : 'earnings:peerComparison.topCompetitors.expandRow',
                            { name }
                          )}
                          aria-expanded={isExpanded}
                          onClick={onChevronClick}
                        />
                      </TableCell>
                      {columns.map(column => (
                        <TableCell
                          key={column.key}
                          className={clsx(styles.cell, column.className)}
                          alignment={column.alignment}
                          isNumber={false}
                        >
                          {column.cell(row)}
                        </TableCell>
                      ))}
                    </tr>
                    {isExpanded && (
                      <tr className={styles.detailRow}>
                        <TableCell
                          className={styles.detailCell}
                          colspan={COLUMN_COUNT}
                          alignment="start"
                          isNumber={false}
                        >
                          <div className={styles.detail}>
                            {insightSummary ? (
                              <p className={styles.insightSummary}>
                                {insightSummary}
                              </p>
                            ) : (
                              <p
                                className={clsx(
                                  styles.insightSummary,
                                  styles.placeholder
                                )}
                              >
                                {t('earnings:noDataToDisplay')}
                              </p>
                            )}
                            {hasEarningsInsight && (
                              <ButtonLink
                                label={t(
                                  'earnings:peerComparison.topCompetitors.readEarningsInsight'
                                )}
                                onClick={onReadInsightClick}
                              />
                            )}
                          </div>
                        </TableCell>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </RevampTable>
      </div>
    </section>
  );
};
